import { useEffect, useMemo, useRef, useState } from "react";
import {
  API_ROUTES,
  CASES,
  DEFAULT_SKIN_ID,
  RARITY_LABEL,
  SKIN_CATALOG,
  findSkin,
  isBuyable,
  type ShopMutationResponse,
  type ShopOpenResponse,
  type ShopStateResponse,
  type SkinDef,
  type SkinRarity,
} from "@ziolo/shared";
import { Button } from "@ziolo/ui";
import { apiRequest, errorMessage } from "../../lib/apiClient.js";
import { BjCardView } from "../blackjack/BjCards.js";

const FACE = { suit: "H" as const, value: "A" as const };
const BACK = { hidden: true as const };
const RARITY_ORDER: SkinRarity[] = ["Common", "Uncommon", "Rare", "Epic", "Legendary", "Special"];

type Tab = "cases" | "skins";

function localShop(chips: number, keys: number): ShopStateResponse {
  return {
    chips,
    keys,
    equipped: DEFAULT_SKIN_ID,
    owned: [DEFAULT_SKIN_ID],
    catalog: SKIN_CATALOG,
    cases: CASES,
  };
}

export function ShopPanel({
  variant = "page",
  onClose,
  onWalletChange,
  initialChips = 0,
  initialKeys = 0,
}: {
  variant?: "page" | "modal";
  onClose?: () => void;
  onWalletChange?: (chips: number, keys: number, equipped?: string) => void;
  initialChips?: number;
  initialKeys?: number;
}) {
  const [state, setState] = useState<ShopStateResponse>(() => localShop(initialChips, initialKeys));
  const [synced, setSynced] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("cases");
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState<ShopOpenResponse | null>(null);
  const [reelDone, setReelDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiRequest<ShopStateResponse>(API_ROUTES.shop.catalog)
      .then((next) => {
        if (cancelled) return;
        setState({
          ...next,
          catalog: SKIN_CATALOG,
          cases: CASES,
        });
        setError(null);
        setSynced(true);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(errorMessage(cause));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function applyWallet(next: ShopMutationResponse) {
    setState((prev) => ({ ...prev, ...next, catalog: SKIN_CATALOG, cases: CASES }));
    onWalletChange?.(next.chips, next.keys, next.equipped);
  }

  async function buy(skinId: string) {
    if (busy || !synced) return;
    setBusy(true);
    setError(null);
    try {
      const result = await apiRequest<ShopMutationResponse>(API_ROUTES.shop.buy, {
        method: "POST",
        body: { skinId },
      });
      applyWallet(result);
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function equip(skinId: string) {
    if (busy || !synced) return;
    setBusy(true);
    setError(null);
    try {
      const result = await apiRequest<ShopMutationResponse>(API_ROUTES.shop.equip, {
        method: "POST",
        body: { skinId },
      });
      applyWallet(result);
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function openCase(caseId: "gold" | "skins") {
    if (busy || !synced) return;
    setBusy(true);
    setError(null);
    setReelDone(false);
    try {
      const result = await apiRequest<ShopOpenResponse>(API_ROUTES.shop.open, {
        method: "POST",
        body: { caseId },
      });
      setOpening(result);
      applyWallet(result);
    } catch (cause: unknown) {
      setError(errorMessage(cause));
      setBusy(false);
    }
  }

  const grouped = useMemo(
    () =>
      RARITY_ORDER.map((rarity) => ({
        rarity,
        skins: SKIN_CATALOG.filter((skin) => skin.rarity === rarity),
      })).filter((group) => group.skins.length > 0),
    [],
  );

  const locked = busy || !synced;

  return (
    <div className={`shop ${variant === "modal" ? "shop--modal" : ""}`}>
      <header className="shop__head">
        <h2>Sklep</h2>
        <p className="shop__wallet">
          Żetony <strong>{state.chips}</strong>
          <span aria-hidden="true"> · </span>
          Klucze <strong>{state.keys}</strong>
        </p>
        {variant === "modal" && onClose ? (
          <Button size="sm" onClick={onClose}>
            Zamknij
          </Button>
        ) : null}
      </header>

      <div className="shop__tabs">
        <button type="button" className={tab === "cases" ? "is-on" : ""} onClick={() => setTab("cases")}>
          Skrzynie
        </button>
        <button type="button" className={tab === "skins" ? "is-on" : ""} onClick={() => setTab("skins")}>
          Skiny
        </button>
      </div>

      {error ? <p className="shop__error">{error}</p> : null}

      {tab === "cases" ? (
        <div className="shop__cases">
          {CASES.map((crate) => (
            <article key={crate.id} className={`shop-case shop-case--${crate.id}`}>
              <h3>{crate.name}</h3>
              <p>{crate.type === "chips" ? "Losowe żetony" : "Losowy skin kart"}</p>
              <p className="shop-case__cost">1 klucz</p>
              <Button
                variant="primary"
                disabled={locked || state.keys < 1}
                onClick={() => void openCase(crate.id)}
              >
                Otwórz
              </Button>
            </article>
          ))}
        </div>
      ) : null}

      {tab === "skins" ? (
        <div className="shop__skins">
          {grouped.map((group) => (
            <section key={group.rarity}>
              <h3 className={`shop-rarity shop-rarity--${group.rarity.toLowerCase().replace(" ", "-")}`}>
                {RARITY_LABEL[group.rarity]}
              </h3>
              <ul className="shop-grid">
                {group.skins.map((skin) => (
                  <li key={skin.id}>
                    <SkinTile
                      skin={skin}
                      owned={state.owned.includes(skin.id)}
                      equipped={state.equipped === skin.id}
                      busy={locked}
                      chips={state.chips}
                      onBuy={() => void buy(skin.id)}
                      onEquip={() => void equip(skin.id)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      ) : null}

      {opening ? (
        <CaseOverlay
          result={opening}
          done={reelDone}
          onFinished={() => {
            setReelDone(true);
            setBusy(false);
          }}
          onClose={() => {
            setOpening(null);
            setReelDone(false);
          }}
        />
      ) : null}
    </div>
  );
}

function SkinTile({
  skin,
  owned,
  equipped,
  busy,
  chips,
  onBuy,
  onEquip,
}: {
  skin: SkinDef;
  owned: boolean;
  equipped: boolean;
  busy: boolean;
  chips: number;
  onBuy: () => void;
  onEquip: () => void;
}) {
  const buyable = isBuyable(skin);
  return (
    <article
      className={`shop-tile ${equipped ? "shop-tile--on" : ""} ${owned ? "shop-tile--owned" : ""}`}
      data-rarity={skin.rarity}
    >
      <div className="shop-preview">
        <div className="shop-preview__stage">
          <div className="shop-preview__card shop-preview__card--face">
            <BjCardView card={FACE} skinId={skin.id} animated={false} />
          </div>
          <div className="shop-preview__card shop-preview__card--back">
            <BjCardView card={BACK} skinId={skin.id} animated={false} />
          </div>
        </div>
      </div>
      <h4>{skin.name}</h4>
      <p className="shop-tile__meta">
        {RARITY_LABEL[skin.rarity]}
        {skin.price !== null && skin.price > 0 ? ` · ${skin.price}` : skin.price === null ? " · tylko skrzynia" : " · darmowy"}
      </p>
      {equipped ? (
        <span className="shop-tile__badge">Założony</span>
      ) : owned ? (
        <Button size="sm" disabled={busy} onClick={onEquip}>
          Załóż
        </Button>
      ) : buyable ? (
        <Button
          size="sm"
          variant="primary"
          disabled={busy || chips < (skin.price ?? Infinity)}
          onClick={onBuy}
        >
          Kup
        </Button>
      ) : (
        <span className="shop-tile__badge">Tylko ze skrzyni</span>
      )}
    </article>
  );
}

const REEL_ITEM = 140;
const REEL_GAP = 4;
const REEL_STEP = REEL_ITEM + REEL_GAP;
const REEL_MS = 6400;
const CASE_TITLE = { gold: "Złota Skrzynia", skins: "Skrzynia Skinów" } as const;

function CaseOverlay({
  result,
  done,
  onFinished,
  onClose,
}: {
  result: ShopOpenResponse;
  done: boolean;
  onFinished: () => void;
  onClose: () => void;
}) {
  const windowRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef(0);
  const finishedRef = useRef(false);
  const [shift, setShift] = useState(0);
  const [moving, setMoving] = useState(false);
  const [landed, setLanded] = useState(done);

  function finish() {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setLanded(true);
    onFinished();
  }

  function skip() {
    setMoving(false);
    setShift(targetRef.current);
    finish();
  }

  useEffect(() => {
    const windowW = windowRef.current?.clientWidth ?? 720;
    const jitter = (Math.random() - 0.5) * (REEL_ITEM - 28);
    const target = result.winnerIndex * REEL_STEP + REEL_ITEM / 2 - windowW / 2 + jitter;
    targetRef.current = target;
    const start = window.requestAnimationFrame(() => {
      setMoving(true);
      setShift(target);
    });
    const timer = window.setTimeout(finish, REEL_MS);
    return () => {
      window.cancelAnimationFrame(start);
      window.clearTimeout(timer);
    };
    // finish/onFinished would retrigger the spin
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.winnerIndex]);

  const drop = result.drop;
  const skinName = drop.type === "skin" ? (findSkin(drop.skinId)?.name ?? drop.skinId) : "";
  const label =
    drop.type === "chips"
      ? `+${drop.amount} żetonów`
      : drop.duplicate
        ? `Duplikat (${skinName}) — +${drop.refund} żetonów`
        : `Skin: ${skinName}`;

  return (
    <div className="shop-overlay" role="dialog" aria-modal="true" aria-label="Otwarcie skrzyni">
      <div className="shop-overlay__panel">
        <p className="shop-overlay__case">{CASE_TITLE[result.caseId]}</p>
        <div className="shop-reel">
          <div className="shop-reel__line" />
          <div className="shop-reel__window" ref={windowRef}>
            <ul
              className={`shop-reel__track ${landed ? "is-landed" : ""}`}
              style={{
                transform: `translateX(${-shift}px)`,
                transition: moving ? `transform ${REEL_MS}ms cubic-bezier(0.08, 0.72, 0, 1)` : "none",
              }}
            >
              {result.reel.map((item, index) => (
                <li
                  key={`${item.kind}-${index}`}
                  data-rarity={item.rarity}
                  className={`shop-reel__item ${index === result.winnerIndex ? "is-win" : ""} ${
                    landed && index === result.winnerIndex ? "is-hit" : ""
                  }`}
                >
                  {item.kind === "chips" ? (
                    <div className="shop-reel__chips">
                      <span className="shop-reel__chips-amt">+{item.amount}</span>
                      <span className="shop-reel__chips-lbl">żetony</span>
                    </div>
                  ) : (
                    <BjCardView card={BACK} skinId={item.skinId} animated={false} />
                  )}
                  <span className="shop-reel__name">
                    {item.kind === "chips" ? RARITY_LABEL[item.rarity] : (findSkin(item.skinId)?.name ?? item.skinId)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        {landed ? (
          <div className="shop-overlay__result" data-rarity={drop.rarity}>
            <p>{label}</p>
            <p className="shop-overlay__rarity">
              {RARITY_LABEL[drop.rarity]} · żetony {result.chips} · klucze {result.keys}
            </p>
            <Button variant="primary" onClick={onClose}>
              OK
            </Button>
          </div>
        ) : (
          <div className="shop-overlay__spin">
            <p className="hub-hint">Otwieranie…</p>
            <Button size="sm" onClick={skip}>
              Pomiń
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
